<script setup lang="ts">

import { middleTruncate } from '@/helpers/utils'
import { T, useTranslate } from '@tolgee/vue'
import BaseButton from '@/components/base/BaseButton.vue'
import { ref } from 'vue'
import { useClipboard } from '@/composables/helpers/clipboard'
import CrossIcon from '@/components/icons/CrossIcon.vue'
import CheckCirclePinkBorder from '@/components/icons/CheckCirclePinkBorder.vue'
import CopyIcon from '@/components/icons/CopyIcon.vue'
import { useDisconnectWallet, useWalletConnect } from '@/composables/game/wallet-connect'
import WalletDisconnectModal from '@/components/modals/WalletDisconnectModal.vue'

const props = withDefaults(defineProps<{
  shortButton?: boolean
  confirmDisconnect?: boolean
  disableDisconnect?: boolean
}>(), {
  shortButton: true,
  confirmDisconnect: true,
  disableDisconnect: false,
})


const emit = defineEmits<{
  (e: 'disconnectDone'): void
}>()

const { t } = useTranslate()
const { copyToClipboard } = useClipboard()
const showWalletDisconnectModal = ref(false)
const { userFriendlyAddress, rawAddress } = useWalletConnect()
const { disconnectWallet, isDisconnecting } = useDisconnectWallet()

function copyWalletAddress() {
  copyToClipboard(rawAddress.value, t.value('wallet.wallet_address_copied', 'Wallet address copied!'))
}

async function onDisconnect() {
  if (props.confirmDisconnect) {
    showWalletDisconnectModal.value = true
    return
  }
  await disconnectWallet()
  emit('disconnectDone')
}
</script>
<template>
  <div class="flex flex-col gap-y-3">
    <div
      class="flex items-center gap-x-3 w-full"
    >
      <BaseButton
        v-if="shortButton && !disableDisconnect"
        class="w-[60px]"
        button-class="gap-x-0"
        @click="onDisconnect"
      >
        <template #leftIcon>
          <CrossIcon />
        </template>
      </BaseButton>
      <div class="flex-1 flex items-center justify-between gap-x-2 px-4 py-2 rounded-xl bg-dark-top-layer h-[60px]">
        <div class="flex items-center gap-x-2">
          <div class="text-base font-semibold">
            {{ middleTruncate(userFriendlyAddress, 11) }}
          </div>
          <CopyIcon
            class="w-4 h-4 min-w-[16px]"
            @click="copyWalletAddress"
          />
        </div>
        <div class="flex items-center gap-x-2">
          <CheckCirclePinkBorder class="w-4 h-4 min-w-[16px]" />
          <div class="text-teal-primary text-sm font-semibold">
            <T
              key-name="wallet.connected"
              default-value="Connected"
            />
          </div>
        </div>
      </div>
    </div>
    <BaseButton
      v-if="!shortButton && !disableDisconnect"
      class="w-full"
      :loading="isDisconnecting"
      @click="onDisconnect"
    >
      <T
        key-name="wallet.disconnect_wallet"
        default-value="Disconnect wallet"
      />
    </BaseButton>
  </div>
  <WalletDisconnectModal
    v-if="showWalletDisconnectModal"
    :show="showWalletDisconnectModal"
    @hide="showWalletDisconnectModal = false"
  />
</template>